import { InjectionToken, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { SearchInputBarComponent } from '@shared/base-search-bar/components/search-input-bar/search-input-bar.component';
import { SearchPlaceInputComponent } from '@shared/base-search-bar/components/search-place-input/search-place-input.component';
import { SearchBarInputComponent } from '@shared/base-search-bar/components/search-bar-input/search-bar-input.component';
import {
  SearchBarPassengerSelectionComponent
} from '@shared/base-search-bar/components/search-bar-passenger-selection/search-bar-passenger-selection.component';
import { SearchBarOperatorComponent } from '@shared/base-search-bar/components/search-bar-operator/search-bar-operator.component';
import { GlobalPipesModule } from '@app/pipes/global-pipes.module';
import { PassengerInputPipe } from '@shared/base-search-bar/pipes/passenger-input.pipe';
import { BtnLoaderModule } from '@ux/components/btn-loader/btn-loader.module';
import { SearchBarRoomComponent } from '@shared/base-search-bar/components/search-bar-room/search-bar-room.component';
import { searchBarFeatureName } from '@shared/base-search-bar/store/state/search-bar.state';
import { searchBarRootReducer } from '@shared/base-search-bar/store/reducers';
import { FlightResultsModuleState } from '@flight-results/store/state/flight-results-module.state';
import { StorageEffects } from '@shared/base-search-bar/store/effects/storage.effects';
import { StorageServices } from '@shared/base-search-bar/services/storage.services';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { SwipeUpModule } from '@ux/components/swipe-up/swipe-up.module';
import { DropdownSelectModule } from '@ux/components/dropdown-select/dropdown-select.module';
import {
  SearchBarCalendarComponent
} from '@shared/base-search-bar/components/calendar/components/search-bar-calendar/search-bar-calendar.component';
import {
  SearchBarChildAgesComponent
} from '@shared/base-search-bar/components/search-bar-child-ages/search-bar-child-ages.component';
import {
  SearchBarMobileCalendarContainer
} from '@shared/base-search-bar/components/calendar/components/search-bar-mobile-calendar/search-bar-mobile-calendar.container';
import {
  CalendarInputsComponent
} from '@shared/base-search-bar/components/calendar/components/calendar-inputs/calendar-inputs.component';
import {
  CalendarHeaderComponent
} from '@shared/base-search-bar/components/calendar/components/calendar-header/calendar-header.component';
import { TooltipModule } from '../tooltip/tooltip.module';
import { PlaceAirportsPipe } from '@shared/base-search-bar/pipes/place-airports.pipe';
import { AirportsVmPipe } from '@shared/base-search-bar/pipes/airports-vm.pipe';
import {
  SearchBarMobileTimepickerComponent
  // eslint-disable-next-line max-len
} from '@shared/base-search-bar/components/timepicker/components/search-bar-mobile-timepicker/search-bar-mobile-timepicker.component';
import {
  SearchInputAllPlacesComponent
} from '@shared/base-search-bar/components/search-input-all-places/search-input-all-places.component';
import {
  SearchBarPopularDestinationsComponent
} from '@shared/base-search-bar/components/search-bar-popular-destinations/search-bar-popular-destinations.component';
import { ImageModule } from '@ux/components/image/image.module';
import {
  SearchInputDestinationComponent
} from '@shared/base-search-bar/components/search-input-destination/search-input-destination.component';
import { TimepickerModule } from '@shared/timepicker/timepicker.module';
import { DebounceDirectiveModule } from '@ux/directives/debounce/debounce-directive.module';
import { ButtonDsComponent } from '@design-system/components/molecules/button/button.component';
import { FixedSurfaceModule } from '@ux/components/fixed-surface/fixed-surface.module';
import { DateCalendarDsComponent } from '@design-system/components/organisms/date-calendar/date-calendar.component';
import { BackgroundSurfaceDsComponent } from '@design-system/components/atoms/background-surface/background-surface.component';
import { ButtonSnippetDsComponent } from '@design-system/components/molecules/button-snippet/button-snippet.component';
import { ButtonLinkDsComponent } from '@design-system/components/molecules/button-link/button-link.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { DividerDsComponent } from '@design-system/components/atoms/divider/divider.component';
import { LoaderRingDsComponent } from '@design-system/components/atoms/loader-ring/loader-ring.component';
import { RadioButtonDsComponent } from '@design-system/components/organisms/radio-button/radio-button.component';
import { DropDownViewModule } from '@ux/components/drop-down-view/drop-down-view.module';
import { InputTextDsComponent } from '@design-system/components/molecules/input-text/input-text.component';
import {
  SearchInputAutocompleteComponent
} from '@shared/base-search-bar/components/search-input-autocomplete/search-input-autocomplete.component';
import { ImageDsComponent } from '@design-system/components/molecules/image/image.component';
import { DataPickerDsComponent } from '@design-system/components/organisms/data-picker/data-picker.component';
import {
  SearchInputActivityComponent
} from '@shared/base-search-bar/components/search-input-activity/search-input-activity.component';

export const BASE_SEARCH_BAR_REDUCER_TOKEN = new InjectionToken<
  ActionReducerMap<FlightResultsModuleState>
>('Feature BaseSearchBarModule Reducers');

@NgModule({
  declarations: [
    SearchPlaceInputComponent,
    SearchBarInputComponent,
    SearchBarPassengerSelectionComponent,
    SearchBarOperatorComponent,
    SearchBarCalendarComponent,
    SearchBarMobileCalendarContainer,
    SearchInputBarComponent,
    PassengerInputPipe,
    PlaceAirportsPipe,
    AirportsVmPipe,
    SearchBarRoomComponent,
    SearchBarChildAgesComponent,
    CalendarInputsComponent,
    CalendarHeaderComponent,
    SearchBarMobileTimepickerComponent,
    SearchInputAllPlacesComponent,
    SearchBarPopularDestinationsComponent,
    SearchInputDestinationComponent,
    SearchInputAutocompleteComponent,
    SearchInputActivityComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    GlobalPipesModule,
    TranslocoModule,
    BtnLoaderModule,
    SwipeUpModule,
    DropdownSelectModule,
    StoreModule.forFeature(searchBarFeatureName, BASE_SEARCH_BAR_REDUCER_TOKEN),
    EffectsModule.forFeature([StorageEffects]),
    TooltipModule,
    ImageModule,
    TimepickerModule,
    DebounceDirectiveModule,
    ButtonDsComponent,
    FixedSurfaceModule,
    DateCalendarDsComponent,
    BackgroundSurfaceDsComponent,
    ButtonSnippetDsComponent,
    ButtonLinkDsComponent,
    TypographyDsComponent,
    IconDsComponent,
    DividerDsComponent,
    LoaderRingDsComponent,
    RadioButtonDsComponent,
    DropDownViewModule,
    InputTextDsComponent,
    ImageDsComponent,
    DataPickerDsComponent,
  ],
  exports: [
    SearchPlaceInputComponent,
    SearchBarInputComponent,
    SearchBarPassengerSelectionComponent,
    SearchBarOperatorComponent,
    SearchBarCalendarComponent,
    SearchBarMobileCalendarContainer,
    SearchInputBarComponent,
    SearchInputAllPlacesComponent,
    PassengerInputPipe,
    PlaceAirportsPipe,
    AirportsVmPipe,
    SearchBarRoomComponent,
    CalendarInputsComponent,
    CalendarHeaderComponent,
    SearchBarMobileTimepickerComponent,
    SearchInputDestinationComponent,
    SearchBarPopularDestinationsComponent,
    SearchInputAutocompleteComponent,
    SearchInputActivityComponent,
  ],
  providers: [
    {
      provide: BASE_SEARCH_BAR_REDUCER_TOKEN,
      useValue: searchBarRootReducer
    },
    BaseSearchBarFacade,
    StorageServices
  ]
})
export class BaseSearchBarModule { }
